/**
 * Brand SVG icons for tracking providers.
 *
 * Inline SVGs (no network fetch) so they render instantly inside the
 * Builder's tracking sections. Paths are vendored from
 * https://simpleicons.org (CC0) and adapted to the brand's primary
 * colours so each card header has an instantly recognisable mark.
 *
 * Each icon ships its own brand-coloured rounded background so callers
 * can drop them in without re-thinking layout/colour per provider.
 */

import React from 'react';

interface IconProps {
  className?: string;
}

const Wrap: React.FC<{
  bg: string;
  /** Optional fixed text colour for the inner glyph; defaults to white. */
  fg?: string;
  className?: string;
  /** Outer box edge in px. Default 28 (matches historical chip size). */
  box?: number;
  /** Corner radius utility; default rounded-md. */
  rounded?: string;
  children: React.ReactNode;
}> = ({
  bg,
  fg = '#ffffff',
  className = '',
  box = 28,
  rounded = 'rounded-md',
  children,
}) => (
  <span
    className={
      `inline-flex items-center justify-center ${rounded} flex-shrink-0 ` +
      className
    }
    style={{ background: bg, color: fg, width: box, height: box }}
  >
    {children}
  </span>
);

export const FacebookBrandIcon: React.FC<IconProps> = ({ className }) => (
  <Wrap bg="#1877F2" className={className}>
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  </Wrap>
);

export const MetaBrandIcon: React.FC<IconProps> = ({ className }) => (
  // Meta's infinity-style mark from simpleicons.org/meta. Rendered on
  // Meta's signature blue→purple gradient so the chip stays on-brand.
  <Wrap bg="linear-gradient(135deg, #0064E0 0%, #7E26FF 100%)" className={className}>
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.157 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a3.1 3.1 0 0 1 1.088-.285z" />
    </svg>
  </Wrap>
);

export const TikTokBrandIcon: React.FC<IconProps> = ({ className }) => (
  <Wrap bg="#000000" className={className}>
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
    </svg>
  </Wrap>
);

export const GoogleAnalyticsBrandIcon: React.FC<IconProps> = ({ className }) => (
  <Wrap bg="#FFFFFF" className={className}>
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="#F9AB00"
        d="M22.84 2.998v17.999a2.983 2.983 0 0 1-5.967.092V3.066a2.985 2.985 0 0 1 2.62-2.96 2.983 2.983 0 0 1 3.347 2.892z"
      />
      <path
        fill="#E37400"
        d="M4.117 17.997a2.983 2.983 0 1 1 0 5.966 2.983 2.983 0 0 1 0-5.966zm7.882-9.014a3.057 3.057 0 0 0-3.052 3.144v8.121a2.999 2.999 0 0 0 5.971.34V12.13a3 3 0 0 0-2.92-3.147z"
      />
    </svg>
  </Wrap>
);

export const GoogleTagManagerBrandIcon: React.FC<
  IconProps & { box?: number; glyph?: number; rounded?: string }
> = ({ className, box = 28, glyph = 18, rounded = 'rounded-md' }) => (
  // Full-colour GTM mark (diamond + lower dot) — geometry from the
  // Iconify `logos:google-tag-manager` set, which matches Google's
  // product icon treatment. Older Simple Icons 9.x 24×24 paths were
  // visually off when scaled; 256× viewBox keeps proportions correct.
  <Wrap bg="#FFFFFF" className={className} box={box} rounded={rounded}>
    <svg
      viewBox="0 0 256 256"
      width={glyph}
      height={glyph}
      aria-hidden="true"
    >
      <path
        fill="#8AB4F8"
        d="m150.262 245.516l-44.437-43.331l95.433-97.454l46.007 45.091z"
      />
      <path
        fill="#4285F4"
        d="M150.45 53.938L106.176 8.731L9.36 104.629c-12.48 12.48-12.48 32.713 0 45.207l95.36 95.986l45.09-42.182l-72.654-76.407z"
      />
      <path
        fill="#8AB4F8"
        d="m246.625 105.37l-96-96c-12.494-12.494-32.756-12.494-45.25 0s-12.495 32.757 0 45.252l96 96c12.494 12.494 32.756 12.494 45.25 0s12.495-32.757 0-45.251"
      />
      <circle cx="127.265" cy="224.731" r="31.273" fill="#246FDB" />
    </svg>
  </Wrap>
);
